import type { NpcDraft } from '@/lib/npc-draft';
import { wordCount } from '@/lib/npc-labels';
import type { NpcBlockInput } from '@matter_extractor/shared';
import { composeNpcBlock } from '@matter_extractor/shared';

/**
 * « Ce que le joueur recevra » (npc.md §3.3): the block of the NPC as a replay would serve it,
 * composed of the DRAFT on screen by the pure function of `shared` - the one that decides what
 * travels, which is why this file composes nothing itself.
 *
 * The fields are read as the schema would store them (trimmed, an empty address is none) and the
 * sources that are not sources yet (no title, a blank text) are left out, as `normalized` leaves
 * the whole content out: what is on screen is what the first save would serve.
 */

/** How many characters of a source's text the folded preview keeps (the mockup shows ~3 lines). */
export const BLOCK_PREVIEW_CHARS = 280;

/** The block input of a draft, as the first save would store it. */
export function blockInputOf(draft: NpcDraft): NpcBlockInput {
  return {
    who: draft.who.trim(),
    firstName: draft.firstName.trim(),
    howToPlay: draft.howToPlay.trim(),
    sources: draft.sources
      .filter((source) => source.title.trim() !== '' && source.text.trim() !== '')
      .map((source) => ({
        title: source.title.trim(),
        url: source.url.trim() === '' ? null : source.url.trim(),
        text: source.text,
      })),
  };
}

/** The whole block, word for word what the player receives after the rules and the mode. */
export function npcBlockOf(draft: NpcDraft): string {
  return composeNpcBlock(blockInputOf(draft));
}

/**
 * The folded block: each source's text cut at `BLOCK_PREVIEW_CHARS` and followed by
 * « (la suite, 872 mots) », the count of the words that are folded away. A short text is kept whole.
 * The unfolded view is `npcBlockOf`, so that nothing is ever shown that is not served.
 */
export function npcBlockPreview(draft: NpcDraft): string {
  const input = blockInputOf(draft);
  return composeNpcBlock({
    ...input,
    sources: input.sources.map((source) => {
      if (source.text.length <= BLOCK_PREVIEW_CHARS) return source;
      const kept = source.text.slice(0, BLOCK_PREVIEW_CHARS).trimEnd();
      const folded = wordCount(source.text.slice(BLOCK_PREVIEW_CHARS));
      return { ...source, text: `${kept}... (la suite, ${folded} mots)` };
    }),
  });
}

/** Does folding hide anything? Then the view offers « Tout afficher ». */
export function npcBlockIsFolded(draft: NpcDraft): boolean {
  return blockInputOf(draft).sources.some((source) => source.text.length > BLOCK_PREVIEW_CHARS);
}
